import * as THREE from 'three';
import { type Labyrinth, TILE_SIZE } from '../labyrinth/Labyrinth';
import { LabyrinthMesh, type LightmapBindings, type WallPulseBindings } from './LabyrinthMesh';

const TILT_DEG = 65;
const TILT_RAD = (TILT_DEG * Math.PI) / 180;
const FOV_DEG  = 60;
const MAX_DPR  = 2;

export interface TileHit { x: number; y: number }

export class ThreeRenderer {
  readonly canvas: HTMLCanvasElement;
  private readonly renderer: THREE.WebGLRenderer;
  private readonly scene = new THREE.Scene();
  private readonly camera = new THREE.PerspectiveCamera(FOV_DEG, 1, 1, 10000);
  private readonly raycaster = new THREE.Raycaster();
  private readonly tmpNDC = new THREE.Vector2();
  private readonly tmpHit = new THREE.Vector3();
  private readonly groundPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);

  private labMesh: LabyrinthMesh | null = null;
  private _enabled = false;

  constructor() {
    this.canvas = document.createElement('canvas');
    const s = this.canvas.style;
    s.position = 'fixed';
    s.inset = '0';
    s.width = '100%';
    s.height = '100%';
    s.zIndex = '0';
    s.display = 'none';
    s.pointerEvents = 'none';
    document.body.insertBefore(this.canvas, document.body.firstChild);

    this.renderer = new THREE.WebGLRenderer({ canvas: this.canvas, antialias: true });
    this.applyPixelRatio();
    // ACES gives the scene a punchy S-curve (lifted highlights, crushed shadows) and
    // exposure >1 brightens overall — together this is the "bright + contrasty" pass.
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.35;
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.setClearColor(0x161616, 1);

    this.resize();
  }

  setLabyrinth(
    lab: Labyrinth,
    lightmap: LightmapBindings,
    wallPulse: WallPulseBindings,
  ): LabyrinthMesh {
    this.clearLabyrinth();
    this.labMesh = new LabyrinthMesh(lab, lightmap, wallPulse);
    this.scene.add(this.labMesh.group);
    return this.labMesh;
  }

  get mesh(): LabyrinthMesh | null { return this.labMesh; }

  add(obj: THREE.Object3D): void { this.scene.add(obj); }
  remove(obj: THREE.Object3D): void { this.scene.remove(obj); }

  private clearLabyrinth(): void {
    if (!this.labMesh) return;
    this.scene.remove(this.labMesh.group);
    this.labMesh.dispose();
    this.labMesh = null;
  }

  setCameraTarget(camWorldTileX: number, camWorldTileY: number, zoom: number): void {
    const cx = camWorldTileX * TILE_SIZE;
    const cz = camWorldTileY * TILE_SIZE;
    const fovRad = (this.camera.fov * Math.PI) / 180;
    const screenH = window.innerHeight;
    const dist = (screenH / 2) / Math.tan(fovRad / 2) / Math.max(zoom, 0.001);
    const eyeY    = Math.sin(TILT_RAD) * dist;
    const eyeOffZ = Math.cos(TILT_RAD) * dist;
    this.camera.position.set(cx, eyeY, cz + eyeOffZ);
    this.camera.up.set(0, 1, 0);
    this.camera.lookAt(cx, 0, cz);
    this.camera.updateMatrixWorld();
  }

  /** Raycast the cursor against the floor plane, return the tile coord (or null if behind camera). */
  screenToTile(clientX: number, clientY: number): TileHit | null {
    const rect = this.canvas.getBoundingClientRect();
    this.tmpNDC.x = ((clientX - rect.left) / rect.width) * 2 - 1;
    this.tmpNDC.y = -(((clientY - rect.top) / rect.height) * 2 - 1);
    this.raycaster.setFromCamera(this.tmpNDC, this.camera);
    const hit = this.raycaster.ray.intersectPlane(this.groundPlane, this.tmpHit);
    if (!hit) return null;
    return { x: hit.x / TILE_SIZE, y: hit.z / TILE_SIZE };
  }

  /**
   * Same as screenToTile, but with the camera virtually placed at the given target/zoom
   * (used by drag-pan to convert cursor delta into world-space delta against a frozen
   * camera pose). Restores camera state on exit.
   */
  screenToTileAtCamera(camTileX: number, camTileY: number, zoom: number, clientX: number, clientY: number): TileHit | null {
    const savedPos = this.camera.position.clone();
    const savedQuat = this.camera.quaternion.clone();
    this.setCameraTarget(camTileX, camTileY, zoom);
    const hit = this.screenToTile(clientX, clientY);
    this.camera.position.copy(savedPos);
    this.camera.quaternion.copy(savedQuat);
    this.camera.updateMatrixWorld();
    return hit;
  }

  resize(): void {
    const w = window.innerWidth;
    const h = window.innerHeight;
    this.applyPixelRatio();
    this.renderer.setSize(w, h, false);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
  }

  private applyPixelRatio(): void {
    const dpr = Math.min(Math.max(window.devicePixelRatio || 1, 1), MAX_DPR);
    this.renderer.setPixelRatio(dpr);
  }

  setEnabled(enabled: boolean): void {
    this._enabled = enabled;
    this.canvas.style.display = enabled ? 'block' : 'none';
  }

  get enabled(): boolean { return this._enabled; }

  render(): void {
    if (!this._enabled) return;
    this.renderer.render(this.scene, this.camera);
  }

  dispose(): void {
    this.clearLabyrinth();
    this.renderer.dispose();
    this.canvas.remove();
  }
}
